<template>
  <span class="ui-button__container">
    <template v-if="!link">
      <button
        class="ui-button"
        :class="[classes]"
        :disabled="disabled || loading || inactive"
        @click="$emit('click')"
        :aria-label="ariaLabel"
        :aria-labelledby="ariaLabelledBy"
        v-bind="$attrs"
        :style="applyStyles"
      >
        <span :style="{ visibility: loading ? 'hidden' : 'visible' }">
          <slot></slot>
        </span>
        <span class="btn-spinner" v-if="loading">
          <i class="spinner"></i>
        </span>
      </button>
    </template>
    <template v-if="link">
      <template v-if="vueRouter">
        <router-link
          class="ui-button"
          :class="[classes]"
          :disabled="disabled || loading"
          @click="$emit('click')"
          :aria-label="ariaLabel"
          :aria-labelledby="ariaLabelledBy"
          v-bind="$attrs"
          :style="applyStyles"
          :to="(disabled || loading || inactive)? 'javascript:void(0)' : link"
        >
          <span :style="{ visibility: loading ? 'hidden' : 'visible' }">
            <slot></slot>
          </span>
          <span class="btn-spinner" v-if="loading">
            <i class="spinner"></i>
          </span>
        </router-link>
      </template>
      <template v-if="!vueRouter">
        <a
          class="ui-button"
          :class="[classes]"
          :disabled="disabled || loading"
          @click="$emit('click')"
          :href="(disabled || loading || inactive)? 'javascript:void(0)' : link"
          :aria-label="ariaLabel"
          :aria-labelledby="ariaLabelledBy"
          v-bind="$attrs"
          :style="applyStyles"
        >
          <span :style="{ visibility: loading ? 'hidden' : 'visible' }">
            <slot></slot>
          </span>
          <span class="btn-spinner" v-if="loading">
            <i class="spinner"></i>
          </span>
        </a>
      </template>
    </template>
  </span>
</template>

<script>
/* eslint-disable implicit-arrow-linebreak */
/* eslint-disable operator-linebreak */
import themeSetupMixin from '@/mixins/theme';
import { createPalette, lightOrDark } from '@/util/colors';

export default {
  inheritAttrs: false,
  mixins: [themeSetupMixin],
  name: 'UiButton',
  props: {
    /**
     * ClassNames for button
     */
    buttonClasses: {
      type: String,
      default: '',
    },
    /**
     * Button styles
     */
    styles: {
      type: Object,
      default() {
        return {};
      },
    },
    /**
     * Button type. This comprises of three types, cancel, primary and succes
     */
    type: {
      type: String,
    },
    /**
     * Button kind. This comprises of two kind, filled and outlined
     */
    kind: {
      type: String,
      default: 'filled',
      validator: value => ['filled', 'outlined'].indexOf(value) !== -1,
    },
    /**
     * if `not empty`, set button to link tag
     */
    link: {
      type: String,
    },
    /**
     * if `not empty`, overwrite text color
    */
    textColor: {
      type: String,
    },
    /**
     * if `not empty`,overwrite hover text color
    */
    hoverTextColor: {
      type: String,
    },
    /**
     * if `not empty`, overwrite button hover color
    */
    hoverColor: {
      type: String,
    },
    /**
     * Color code
     */
    color: {
      type: String,
      required: false,
    },
    /**
     * Button size
     */
    size: {
      type: String,
      default: 'default',
      validator: value =>
        ['default', 'large', 'small', 'medium', 'xsmall'].indexOf(value) !== -1,
    },
    /**
     * Disable button
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * if set, overwrite button font-weight
     */
    fontWeight: {
      type: Number,
    },
    /**
     * If `true` set link tag to router-link
     */
    vueRouter: {
      type: Boolean,
      default: false,
    },
    /**
     * If `true` set button to inactive
     */
    inactive: {
      type: Boolean,
      default: false,
    },
    /**
     * If `true` enable button loader and disable button
     */
    loading: {
      type: Boolean,
      default: false,
    },
    ariaLabel: {
      type: String,
    },
    ariaLabelledBy: {
      type: String,
    },
  },
  computed: {
    /**
     * Set classNames respectively
    */
    classes() {
      const size = this.size ? `btn-${this.size}` : '';
      const type = this.type ? `btn-${this.type}` : '';
      const kind = this.kind ? `btn-${this.kind}` : '';
      const inactive = this.inactive ? 'btn-inactive' : '';
      const disabled = this.disabled ? 'btn-disabled' : '';
      const loading = this.loading ? 'btn-loading' : '';

      return `ripple ${this.buttonClasses} ${size} ${type} ${kind} ${inactive} ${disabled} ${loading}`;
    },
    applyStyles() {
      if (this.color && this.color !== '') {
        const color = this.color.replace(/#/, '');
        const colorName = `custom-${color}`;
        createPalette({
          custom: {
            [color]: this.color,
          },
        });
        const defaultTextColor = (lightOrDark(this.color) === 'light') ? '#000' : 'var(--brand-white)';
        const hoverTextColor = (this.hoverTextColor && this.hoverColor !== '') ? this.hoverTextColor : defaultTextColor;

        return {
          '--text-filled-color': (this.textColor && this.textColor !== '') ? this.textColor : defaultTextColor,
          '--hover-text-filled-color': hoverTextColor,
          '--background-filled-color': `var(--${colorName})`,
          '--hover-background-filled-color': this.hoverColor || `var(--${colorName}--darken)`,

          '--text-outlined-color': (this.textColor && this.textColor !== '') ? this.textColor : `var(--${colorName})`,
          '--hover-text-outlined-color': hoverTextColor,
          '--background-outlined-color': 'transparent',
          '--hover-background-outlined-color': this.hoverColor || `var(--${colorName})`,
          '--border-outlined-color': `var(--${colorName})`,
          ...this.styles,
        };
      }
      const styles = { ...this.styles };

      if (this.fontWeight) {
        styles['--font-weight'] = this.fontWeight;
      }

      if (this.hoverColor) {
        styles['--hover-background-filled-color'] = this.hoverColor;
        styles['--hover-background-outlined-color'] = this.hoverColor;
      }

      if (this.hoverTextColor) {
        styles['--hover-text-filled-color'] = this.hoverTextColor;
        styles['--hover-text-outlined-color'] = this.hoverTextColor;
      }

      if (this.textColor) {
        styles['--text-filled-color'] = this.textColor;
        styles['--text-outlined-color'] = this.textColor;
      }
      return styles;
    },
  },
};
</script>
<style lang="scss" scoped>
// IN-BUILT THEMES
.ui-button {
  --font-weight: 500
}
.btn-primary {
  --text-filled-color: var(--brand-white);
  --hover-text-filled-color: var(--brand-white);
  --background-filled-color: var(--brand-main);
  --hover-background-filled-color: var(--brand-main--darken);

  --text-outlined-color: var(--brand-main);
  --background-outlined-color: unquote($string: 'transparent');
  --hover-background-outlined-color: var(--brand-main);
  --hover-text-outlined-color: var(--brand-white);
  --border-outlined-color: var(--brand-main);
}

.btn-success {
  --text-filled-color: var(--brand-white);
  --hover-text-filled-color: var(--brand-white);
  --background-filled-color: var(--success-20);
  --hover-background-filled-color: var(--success-20--darken);

  --text-outlined-color: var(--success-20);
  --hover-text-outlined-color: var(--brand-white);
  --background-outlined-color: unquote($string: 'transparent');
  --hover-background-outlined-color: var(--success-20);
  --border-outlined-color: var(--success-20);
}

.btn-cancel {
  --text-filled-color: var(--brand-white);
  --hover-text-filled-color: var(--brand-white);
  --background-filled-color: var(--failed-20);
  --hover-background-filled-color: var(--failed-20--darken);

  --text-outlined-color: var(--failed-20);
  --hover-text-outlined-color: var(--brand-white);
  --background-outlined-color: var(--failed-40);
  --hover-background-outlined-color: var(--failed-20);
  --border-outlined-color: var(--failed-20);
}

@mixin general {
  &:disabled.btn-disabled,
  &.btn-disabled {
    color: var(--primary-20);
    border: 1px solid var(--primary-60);
    background: var(--primary-60);
    opacity: 1;
    // pointer-events: none;
    cursor: not-allowed;
    &:hover {
      opacity: 1;
      background: var(--primary-60) !important;
    }
  }
  &.btn-disabled.ripple:after,
  &.btn-inactive.ripple:after,
  &.btn-loading.ripple:after,
  &:disabled.ripple:after{
    display:none;
  }
}

@mixin btnFilled {
  color: var(--text-filled-color);
  border: 1px solid var(--background-filled-color);
  background: var(--background-filled-color);
}
@mixin btnOutlined {
  color: var(--text-outlined-color);
  background: var(--background-outlined-color);
  border: 1px solid var(--border-outlined-color);
}

button:focus {
  outline: none;
}

.ui-button {
  border-radius: 5px;
  font-weight: var(--font-weight);
  display: inline-block;
  position: relative;
  cursor: pointer;
  text-align: center;
  text-decoration: none;
  &:focus {
    outline: var(--hover-background-filled-color);
  }
  transition: background 250ms ease-in-out, transform 150ms ease;
  -webkit-appearance: none;
  -moz-appearance: none;

  &.btn-default {
    min-width: 125px;
    padding: 18px 36px;
    font-size: 15px;
  }
  &.btn-xsmall {
    min-width: 83px;
    padding: 9px 20px;
    font-size: 12px;
    line-height: 95%;
    & i.spinner {
      height: 12px;
      width: 12px;
    }
  }
  &.btn-small {
    min-width: 49px;
    padding: 14px 28px;
    font-size: 15px;
    line-height: 95%;
    & i.spinner {
      height: 15px;
      width: 15px;
    }
  }
  &.btn-medium {
    min-width: 141px;
    padding: 23px 46px;
    font-size: 15px;
    line-height: 95%;
  }
  &.btn-large {
    min-width: 173px;
    padding: 25px 51px;
    font-size: 20px;
    line-height: 95%;
  }
  @keyframes spinner {
    to {
      transform: rotate(360deg);
    }
  }

  .btn-spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  i.spinner {
    content: '';
    box-sizing: border-box;
    display: inline-block;
    border-radius: 50%;
    border-width: 2px;
    position: relative;
    bottom: -2px;
    border-style: solid;
    height: 20px;
    width: 20px;
    border-top-color: transparent;
    animation: spinner 0.6s linear infinite;
  }
  .icon {
    position: absolute;
  }
  .icon.prefix {
    left: 10px;
  }
  .icon.suffix {
    right: 10px;
  }
  &.btn-filled {
    @include btnFilled;
    &:hover {
      color: var(--hover-text-filled-color);
      background: var(--hover-background-filled-color);
      border-color: var(--hover-background-filled-color);
    }
    &.btn-inactive {
      opacity: 0.5;
      &:hover {
        opacity: 0.5;
        cursor: not-allowed;
        @include btnFilled;
      }
    }
    @include general;
    &:disabled.btn-loading,
    &.btn-loading {
      cursor: not-allowed;
      opacity: 0.5;
      &:hover {
        @include btnFilled;
      }
    }
  }
  &.btn-outlined {
    @include btnOutlined;
    &:hover {
      color: var(--hover-text-outlined-color);
      background: var(--hover-background-outlined-color);
    }
    &.btn-inactive {
      opacity: 0.5;
      &:hover {
        opacity: 0.5;
        cursor: not-allowed;
        @include btnOutlined;
      }
    }
    @include general;
    &:disabled.btn-loading,
    &.btn-loading {
      cursor: not-allowed;
      opacity: 0.5;
      &:hover {
        @include btnOutlined;
      }
    }
  }
}

.ui-button.ripple {
  overflow: hidden;
  display: inline-block;
  position: relative;
}
.ui-button.ripple:after {
  content: '';
  display: inline-block;
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-image: radial-gradient(circle, #000 10%, transparent 10.01%);
  background-image: -o-radial-gradient(circle, #000 10%, transparent 10.01%);
  background-image: -moz-radial-gradient(circle, #000 10%, transparent 10.01%);
  background-image: -webkit-radial-gradient(
    circle,
    #000 10%,
    transparent 10.01%
  );
  background-repeat: no-repeat;
  background-position: left;
  transform: scale(10, 10);
  opacity: 0;
  transition: transform 0.3s, opacity 1s;
}
.ui-button.ripple:active:after {
  transform: scale(0, 0);
  opacity: 0.5;
  transition: 0s;
}
</style>
